import { isEqual, sortBy, groupBy } from "lodash-es";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Auth from "shared/@auth";
import createNotification from "shared/@utils/createNotification";
import normalizeCategory from "../normalizeCategory";

/**
 * Sorts categories by parent and stepNumber and updates stepNumber
 * to be sequential. This fixes issues where the items are in the right order
 * but the numbers are out of wack, or where the items have no numbering at all.
 *
 * NOTE: This step is essential for cleaning the data to avoid problems with sorting
 *
 * @param {category[]} categories - array of categories from the API
 */
function normalizeCategories(categories) {
  const normalized = [];

  Object.values(
    groupBy(categories, (category) => category.parentCategoryID ?? "root")
  ).forEach((siblings) => {
    const validSteps = siblings
      .map((category) => Number(category.stepNumber))
      .filter((stepNumber) => Number.isInteger(stepNumber) && stepNumber > 0);
    let nextStepNumber = Math.max(0, ...validSteps) + 1;

    siblings.forEach((category) => {
      const stepNumber = Number(category.stepNumber);
      normalized.push({
        ...category,
        stepNumber:
          Number.isInteger(stepNumber) && stepNumber > 0
            ? stepNumber
            : nextStepNumber++,
        rowNumber:
          category.parentCategoryID == null ? null : category.rowNumber || 1,
      });
    });
  });

  return sortBy(normalized, "id");
}

export function useSSFData() {
  const { makeAuthedRequest } = Auth.useContainer();

  const queryClient = useQueryClient();
  const [categories, setCategories] = useState([]);
  const [originals, setOriginals] = useState([]);

  const categoriesQuery = useQuery(["categories", "ssf"], () =>
    makeAuthedRequest(`/category-builder/categories/3`).then(async (res) => {
      const json = await res.json();

      if (res.ok) {
        return sortBy(json.map(normalizeCategory), "id");
      } else {
        throw new Error(json.message);
      }
    })
  );

  useEffect(() => {
    if (categoriesQuery.isSuccess) {
      const data = normalizeCategories(categoriesQuery.data);

      setCategories(data);
      setOriginals(categoriesQuery.data);
    }
  }, [categoriesQuery.isSuccess, categoriesQuery.data]);

  const addCategoryMutation = useMutation(
    (category) => {
      const parentCategoryID = category.parentCategoryID ?? null;
      const siblings = categories.filter(
        (existing) => (existing.parentCategoryID ?? null) === parentCategoryID
      );
      const stepNumber =
        Math.max(
          0,
          ...siblings.map((sibling) => Number(sibling.stepNumber) || 0)
        ) + 1;

      return makeAuthedRequest(`/category-builder/categories`, {
        method: "post",
        body: JSON.stringify({
          ...category,
          stepNumber,
          applicationTypeID: 3, // SSF app ID
        }),
      })
        .then(async (res) => {
          const json = await res.json();

          if (res.ok) {
            return json;
          } else {
            throw new Error(json.message);
          }
        })
        .then((categoryId) => {
          if (category._pendingIconUpload) {
            return uploadIcon(category._pendingIconUpload, categoryId);
          }
        });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["cms"]);
        queryClient.invalidateQueries(["categories", "ssf"]);

        createNotification({
          type: "success",
          title: "Added",
          message: "New category added",
          autoDismiss: true,
        });
      },
    }
  );

  const saveMutation = useMutation(
    async () => {
      const added = [];
      const removed = [];
      const updated = [];
      const images = [];

      for (const category of categories) {
        // no id means it hasn't been saved yet
        if (category.id == null) {
          added.push(category);
          continue;
        }

        // queue image upload
        if (category._pendingIconUpload) {
          images.push({
            categoryId: category.id,
            file: category._pendingIconUpload,
          });
        }

        const original = originals.find((o) => o.id === category.id);

        // no original means this one is new
        if (!original) {
          added.push(category);
          continue;
        }

        // deep compare to find out if we need to make an API call
        if (!isEqual(original, category)) {
          updated.push(category);
          continue;
        }
      }

      for (const category of originals) {
        const match = categories.find((c) => c.id === category.id);

        // without a match in the current list means this one has been removed
        if (!match) {
          removed.push(category);
          continue;
        }
      }

      if (categories !== originals) {
        await makeAuthedRequest(`/category-builder/steps`, {
          method: "put",
          body: JSON.stringify(
            categories.map((c) => ({ id: c.id, stepNumber: c.stepNumber }))
          ),
        });
      }

      const requests = [
        ...added.map((category) =>
          makeAuthedRequest(`/category-builder/categories`, {
            method: "post",
            body: JSON.stringify(category),
          })
        ),
        ...updated.map((category) =>
          makeAuthedRequest(`/category-builder/categories`, {
            method: "put",
            body: JSON.stringify(category),
          })
        ),
        ...removed.map((category) =>
          makeAuthedRequest(`/category-builder/categories/${category.id}`, {
            method: "delete",
          })
        ),
        ...images.map(({ file, categoryId }) => uploadIcon(file, categoryId)),
      ];

      return Promise.all(requests);
    },
    {
      onSuccess: () => {
        // Have to blip all CMS data because it comes down together in one request
        queryClient.invalidateQueries(["cms"]);
        queryClient.invalidateQueries(["categories", "ssf"]);

        createNotification({
          type: "success",
          title: "Saved",
          message: "Category changes were successfully saved",
          autoDismiss: true,
        });
      },
    }
  );

  const uploadIcon = async (file, categoryId) => {
    const data = new FormData();
    data.append("file", file);

    return makeAuthedRequest(
      `/category-builder/categories/${categoryId}/thumbnail`,
      {
        headers: {
          "Content-Type": undefined,
        },
        method: "PUT",
        body: data,
      }
    )
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const err = await res.json();
          throw new Error(err.message);
        }
      })
      .then((json) => {
        return json.imagePath;
      });
  };

  return {
    isChanged: !isEqual(categories, normalizeCategories(originals)),
    isSuccess: categoriesQuery.isSuccess,
    isLoading: categoriesQuery.isLoading,
    isError: categoriesQuery.isError,
    categories: categories,
    error: categoriesQuery.error,
    reset: () => setCategories(normalizeCategories(originals)),

    setCategories,
    saveMutation,
    addCategoryMutation,
  };
}
